import { createContext, type ReactNode, useContext } from "react";
import { type AnyUser, type PageData } from "./types.ts";

const pageDataGlobalName = "__PAGE_DATA__";

export function getPageData(): PageData {
  return (globalThis as any)[pageDataGlobalName];
}

export function PageDataScript(): ReactNode {
  const pageDataJson = JSON.stringify(usePageData())
    .replaceAll("<", "\\u003c")
    .replaceAll(">", "\\u003e")
    .replaceAll("&", "\\u0026");
  return (
    <script
      id="page-data"
      dangerouslySetInnerHTML={{
        __html: `var ${pageDataGlobalName} = ${pageDataJson};`,
      }}
    />
  );
}

export const PageDataContext = createContext<PageData>(null!);

export function usePageData(): PageData {
  const value = useContext(PageDataContext);
  if (value == null) {
    throw new Error(
      process.env.NODE_ENV !== "production"
        ? "PageDataContext is missing"
        : undefined,
    );
  }
  return value;
}

/** Who the visitor is, and how they appear to everyone else. */
export type PageIdentity = Pick<PageData, "user" | "publicUser">;

/**
 * Replaces the identity the whole app was served with.
 *
 * The page data is a snapshot the server injects into the document, so a change
 * made on the account page — turning anonymization on or off — would otherwise
 * only be visible on that page: the header, the profile and the race would go
 * on showing the old name until a full reload, which reads as the setting not
 * working at all.
 *
 * Defaults to a no-op so that server rendering, where nothing can change, needs
 * no provider.
 */
export const PageIdentityContext = createContext<
  (identity: PageIdentity) => void
>(() => {});

export function useSetPageIdentity(): (identity: PageIdentity) => void {
  return useContext(PageIdentityContext);
}

export function isPremiumUser(user: AnyUser): boolean {
  return user.id != null && user.premium;
}
